import React, { FC, useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { Button, InfoBlock, Input, Select, Toggle, Typography } from '../../../components'
import { staffObjectStorageService } from '../../../api'
import {
  DisabledReason,
  ObjectUserUpdate,
  StaffObjectUserDetails,
} from '../../../api/staffObjectStorageService'
import { useUi } from '../../../context/ui.context'
import { timeConverterMMDDYYHHMM } from '../../../utils/dates'
import {
  Card,
  CardTitle,
  Cards,
  Content,
  FormActions,
  FormGrid,
  MutedText,
} from './styles'
import { displayValue, getObjectStorageError } from './utils'

type Props = {
  objectUser: StaffObjectUserDetails
  onUpdate: (objectUser: StaffObjectUserDetails) => void
}

const toNumber = (value: string) => Number(value)

const ObjectUserOverview: FC<Props> = ({ objectUser, onUpdate }) => {
  const [t] = useTranslation()
  const { viewAlert } = useUi()
  const [loading, setLoading] = useState(false)
  const unavailable = objectUser.deleted || !objectUser.ceph_uid

  const generalInfo = [
    { id: '1', title: t('ID'), value: objectUser.id, copy: true },
    { id: '2', title: t('CEPH_UID'), value: displayValue(objectUser.ceph_uid), copy: true },
    {
      id: '3',
      title: t('CLIENT'),
      value: objectUser.client.display || objectUser.client.name,
      href: `/clients-users/clients/${objectUser.client.id}`,
    },
    {
      id: '4',
      title: t('SERVICE'),
      value: objectUser.service?.display_name || 'N/A',
      href: objectUser.service ? `/billing/services/${objectUser.service.id}` : undefined,
    },
    { id: '5', title: t('DOMAIN'), value: displayValue(objectUser.service?.domain_name) },
    { id: '6', title: t('BUCKETS'), value: objectUser.bucket_count },
    { id: '7', title: t('S3_KEYS'), value: objectUser.keys_count },
    { id: '8', title: t('CREATED_AT'), value: timeConverterMMDDYYHHMM(objectUser.created_at) },
    { id: '9', title: t('UPDATED_AT'), value: timeConverterMMDDYYHHMM(objectUser.updated_at) },
  ]

  const stateInfo = [
    { id: '1', title: t('DELETED'), value: objectUser.deleted ? t('YES') : t('NO') },
    { id: '2', title: t('DISABLED'), value: objectUser.disabled ? t('YES') : t('NO') },
    {
      id: '3',
      title: t('DISABLED_REASON'),
      value: objectUser.disabled_reason
        ? t(`OBJECT_STORAGE_REASON_${objectUser.disabled_reason.toUpperCase()}`)
        : 'N/A',
    },
    {
      id: '4',
      title: t('SUSPENDED_IN_RGW'),
      value: objectUser.suspended === null ? 'N/A' : objectUser.suspended ? t('YES') : t('NO'),
    },
    { id: '5', title: t('SERVICE_STATUS'), value: displayValue(objectUser.service?.status) },
  ]

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      display_name: objectUser.display_name || '',
      email: objectUser.email || '',
      max_buckets: String(objectUser.max_buckets ?? -1),
      disabled: objectUser.disabled,
      disabled_reason: objectUser.disabled_reason || 'admin_locked',
      user_quota_enabled: objectUser.user_quota?.enabled || false,
      user_quota_max_size_kb: String(objectUser.user_quota?.max_size_kb ?? -1),
      user_quota_max_objects: String(objectUser.user_quota?.max_objects ?? -1),
      bucket_quota_enabled: objectUser.bucket_quota?.enabled || false,
      bucket_quota_max_size_kb: String(objectUser.bucket_quota?.max_size_kb ?? -1),
      bucket_quota_max_objects: String(objectUser.bucket_quota?.max_objects ?? -1),
    },
    validationSchema: yup.object({
      display_name: yup.string().required(`${t('REQUIRED_FIELD')}`),
      email: yup.string().email(`${t('INVALID_EMAIL')}`),
      max_buckets: yup.number().integer().min(-1).required(`${t('REQUIRED_FIELD')}`),
      user_quota_max_size_kb: yup.number().integer().min(-1).required(`${t('REQUIRED_FIELD')}`),
      user_quota_max_objects: yup.number().integer().min(-1).required(`${t('REQUIRED_FIELD')}`),
      bucket_quota_max_size_kb: yup.number().integer().min(-1).required(`${t('REQUIRED_FIELD')}`),
      bucket_quota_max_objects: yup.number().integer().min(-1).required(`${t('REQUIRED_FIELD')}`),
    }),
    onSubmit: async (values) => {
      const payload: ObjectUserUpdate = {
        display_name: values.display_name,
        email: values.email,
        max_buckets: toNumber(values.max_buckets),
        disabled: values.disabled,
        user_quota: {
          enabled: values.user_quota_enabled,
          max_size_kb: toNumber(values.user_quota_max_size_kb),
          max_objects: toNumber(values.user_quota_max_objects),
        },
        bucket_quota: {
          enabled: values.bucket_quota_enabled,
          max_size_kb: toNumber(values.bucket_quota_max_size_kb),
          max_objects: toNumber(values.bucket_quota_max_objects),
        },
      }
      if (values.disabled) {
        payload.disabled_reason = values.disabled_reason as DisabledReason
      }

      try {
        setLoading(true)
        const { data } = await staffObjectStorageService.updateUser(objectUser.id, payload)
        onUpdate(data)
        viewAlert({ severity: 'information', message: t('OBJECT_STORAGE_USER_UPDATED') })
      } catch (error) {
        viewAlert({
          severity: 'error',
          message: getObjectStorageError(error, t('ERROR_OCCURRED')),
        })
      } finally {
        setLoading(false)
      }
    },
  })

  const formikProps = {
    values: formik.values,
    errors: formik.errors,
    touched: formik.touched,
    handleChange: formik.handleChange,
    handleBlur: formik.handleBlur,
  }
  const reasonOptions = [
    { value: 'admin_locked', label: t('OBJECT_STORAGE_REASON_ADMIN_LOCKED') },
    { value: 'need_payment', label: t('OBJECT_STORAGE_REASON_NEED_PAYMENT') },
  ]

  return (
    <Content>
      <Cards>
        <InfoBlock title={t('GENERAL_INFORMATION')} data={generalInfo} customStyles={{ width: '100%' }} />
        <InfoBlock title={t('STATUS')} data={stateInfo} customStyles={{ width: '100%' }} />
      </Cards>
      {unavailable ? (
        <Card>
          <CardTitle>{t('RGW_PROFILE_UNAVAILABLE')}</CardTitle>
          <MutedText>{t('RGW_PROFILE_UNAVAILABLE_DESCRIPTION')}</MutedText>
        </Card>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <Content>
            <Card>
              <CardTitle>{t('RGW_PROFILE')}</CardTitle>
              <FormGrid>
                <Input required title={t('DISPLAY_NAME')} fieldName="display_name" placeholder="" {...formikProps} />
                <Input title={t('EMAIL')} fieldName="email" placeholder="" {...formikProps} />
                <Input
                  required
                  type="number"
                  title={t('MAX_BUCKETS')}
                  fieldName="max_buckets"
                  placeholder="-1"
                  {...formikProps}
                />
              </FormGrid>
            </Card>
            <Card>
              <CardTitle>{t('STAFF_ACCESS_CONTROL')}</CardTitle>
              <Toggle
                title={t('DISABLE_OBJECT_STORAGE_USER')}
                value={formik.values.disabled}
                onChange={() => formik.setFieldValue('disabled', !formik.values.disabled)}
              />
              {formik.values.disabled ? (
                <Select
                  title={t('DISABLED_REASON')}
                  data={reasonOptions}
                  selectedValue={formik.values.disabled_reason}
                  onChange={(value) => formik.setFieldValue('disabled_reason', value)}
                  customStyles={{ maxWidth: '545px' }}
                />
              ) : null}
            </Card>
            <Cards>
              <Card>
                <CardTitle>{t('USER_QUOTA')}</CardTitle>
                <Toggle
                  title={t('ENABLED')}
                  value={formik.values.user_quota_enabled}
                  onChange={() => formik.setFieldValue('user_quota_enabled', !formik.values.user_quota_enabled)}
                />
                <Input
                  required
                  type="number"
                  title={t('MAX_SIZE_KB')}
                  fieldName="user_quota_max_size_kb"
                  placeholder="-1"
                  {...formikProps}
                />
                <Input
                  required
                  type="number"
                  title={t('MAX_OBJECTS')}
                  fieldName="user_quota_max_objects"
                  placeholder="-1"
                  {...formikProps}
                />
                <Typography variant="small">
                  {t('CHECK_ON_RAW')}: {objectUser.user_quota?.check_on_raw ? t('YES') : t('NO')}
                </Typography>
              </Card>
              <Card>
                <CardTitle>{t('BUCKET_QUOTA')}</CardTitle>
                <Toggle
                  title={t('ENABLED')}
                  value={formik.values.bucket_quota_enabled}
                  onChange={() => formik.setFieldValue('bucket_quota_enabled', !formik.values.bucket_quota_enabled)}
                />
                <Input
                  required
                  type="number"
                  title={t('MAX_SIZE_KB')}
                  fieldName="bucket_quota_max_size_kb"
                  placeholder="-1"
                  {...formikProps}
                />
                <Input
                  required
                  type="number"
                  title={t('MAX_OBJECTS')}
                  fieldName="bucket_quota_max_objects"
                  placeholder="-1"
                  {...formikProps}
                />
                <Typography variant="small">
                  {t('CHECK_ON_RAW')}: {objectUser.bucket_quota?.check_on_raw ? t('YES') : t('NO')}
                </Typography>
              </Card>
            </Cards>
            <FormActions>
              <Button
                type="submit"
                variant="primary"
                size="display"
                title={t('SAVE')}
                loading={loading}
              />
            </FormActions>
          </Content>
        </form>
      )}
    </Content>
  )
}

export default ObjectUserOverview
